import React, { useEffect } from "react";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";


// Components
import { Dashboard } from "./components/dashboard/dashboard";
import { StopListing } from "./components/stops/stops-list";
import { StopCreate } from "./components/stops/stops-create";
import { StopView } from "./components/stops/stops-view";
import { TripsListing } from "./components/trips/trips-list";
import { TripsCreate } from "./components/trips/trips-create";
import { TripsView } from "./components/trips/trips-view";
import { TripsCostListing } from "./components/trip-cost/trips-cost";
import { TripCostCreate } from "./components/trip-cost/trip-cost-create";
import { TripCostView } from "./components/trip-cost/trip-cost-view";
import { MilestoneCreate } from "./components/milestone/milestone-create";
import { MilestoneListing } from "./components/milestone/milestone-list";
import { LiveTrack } from "./components/live-view/live-track";
import { TripLiveTrack } from "./components/live-view/trip-live-track";
import { VehicleListing } from "./components/vehicle/vehicle-list";
import { VehicleCreate } from "./components/vehicle/vehicle-create";
import { VehicleView } from "./components/vehicle/vehicle-view";
import CustomerList from "./components/master/customer/list";
import CustomerCreate from "./components/master/customer/create";
import CustomerView from "./components/master/customer/view";
import VendorList from "./components/master/vendors/list";
import VendorCreate from "./components/master/vendors/create";
import VendorView from "./components/master/vendors/view";
import Report from "./components/report/report";
import { useGlobal } from "./GlobalContext";
import { apiCallAdmin } from "./services/api-service";
import UnAuthorized from "./unauthorized";
import ProductListing from "./components/products/product-list";
import ProductCreate from "./components/products/product-create";
import ProductView from "./components/products/product-view";
import HelpQuestions from "./components/common/help";
import Unauthorized from "./components/common/unauthorized";
import { Layout } from "./components/common/Layout";


import { getRolePermissionApi, getRoleTypeApi, validateTokenApi } from "./components/common/common-api";

function App() {
  const accountsUrl = process.env.REACT_APP_ACCOUNTS_URL;
  const environment = process.env.REACT_APP_ENVIRONMENT;

  const { rolePermissionList, setRolePermissionList, setUserType } = useGlobal();

  useEffect(() => {
    const channel = new BroadcastChannel(`auth_channel_mawalog_${environment}`);
    channel.onmessage = () => {
      window.location.reload();
    };
    return () => channel.close();
  }, [environment]);

  useEffect(() => {
    const checkTokenAndFetch = async () => {
      try {
        const response = await validateTokenApi();

        if (response && response.status === 200) {
          getRolePermission();
          getRoleType();
        } else {
          handleInvalidToken();
        }
      } catch (err) {
        handleInvalidToken();
      }
    };

    checkTokenAndFetch();
  }, []);

  const getRolePermission = async () => {
    try {
      const response = await getRolePermissionApi(5);
      if (response && response.status === 200 && response.data.success === true) {
        const permissions = response.data.role_permission ?? [];
        setRolePermissionList(permissions);
        localStorage.setItem("rolePermissionList", JSON.stringify(permissions));
      } else {
        setRolePermissionList([]);
      }
    } catch (err) {
      // Error handled by api-service/interceptor
    }
  };

  const getRoleType = async () => {
    try {
      const response = await getRoleTypeApi();
      if (response && response.status === 200 && response.data.success === true) {
        const uType = response.data.user_type;
        setUserType(uType);
        localStorage.setItem("userType", uType ?? "");
      } else {
        setUserType("");
      }
    } catch (err) {
      // Error handled by api-service/interceptor
    }
  };

  const handleInvalidToken = () => {
    const currentURL = window.location.href;
    const redirectData = {
      redirectPath: currentURL,
      app: "transport",
      ts: Date.now(),
    };
    window.name = JSON.stringify(redirectData);
    document.cookie = "mawalog=; path=/; domain=.mawalog.com; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    localStorage.removeItem("userType");
    localStorage.removeItem("rolePermissionList");
    setUserType("");
    setRolePermissionList([]);
    window.location.href = `${accountsUrl}/login`;
  };

  function checkPermission(menuName, permission) {
    return rolePermissionList.findIndex((element) => element["name"] === menuName && element["menu_permission"] === true && element[permission] === true) > -1 || rolePermissionList.findIndex((element) => element["name"] === menuName && element[permission] === true) > -1;
  }

  const ProtectedRoute = ({ isAllowed }) => {
    return isAllowed ? <Outlet /> : <Navigate to="/unauthorized" />;
  };

  const getFirstAvailableRoute = () => {
    const menuRouteMap = [
      { menu: "Dashboard", route: "/dashboard" },
      { menu: "Stops", route: "/stops" },
      { menu: "Trips", route: "/trips" },
      { menu: "Trip cost", route: "/trip-cost" },
      { menu: "Milestone", route: "/milestones" },
      { menu: "Live view", route: "/livetrack" },
      { menu: "Vehicle", route: "/vehicles" },
      { menu: "Customer", route: "/customers" },
      { menu: "Vendor", route: "/vendor" },
      { menu: "Report", route: "/reports" },
      { menu: "Product", route: "/products" },
    ];

    for (const item of menuRouteMap) {
      if (checkPermission(item.menu, "menu_permission") || checkPermission(item.menu, "list_permission")) {
        return item.route;
      }
    }
    return "/unauthorized";
  };

  const DefaultRoute = () => {
    if (checkPermission("Dashboard", "menu_permission")) {
      return <Dashboard />;
    }
    const firstRoute = getFirstAvailableRoute();
    if (firstRoute === "/dashboard") {
      return <Dashboard />;
    }
    return <Navigate to={firstRoute} replace />;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/help" element={<HelpQuestions />} />

          <Route
            element={
              <Layout>
                <Outlet />
              </Layout>
            }
          >
            <Route path="/" element={<DefaultRoute />} />
            <Route path="/dashboard" element={<DefaultRoute />} />

            {/* Stops */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Stops", "list_permission")} />}>
              <Route path="/stops" element={<StopListing />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Stops", "create_permission")} />}>
              <Route path="/stops/create" element={<StopCreate />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Stops", "view_permission")} />}>
              <Route path="/stops/view" element={<StopView />} />
            </Route>

            {/* Trips */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Trips", "list_permission")} />}>
              <Route path="/trips" element={<TripsListing />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Trips", "create_permission")} />}>
              <Route path="/trips/create" element={<TripsCreate />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Trips", "view_permission")} />}>
              <Route path="/trips/view" element={<TripsView />} />
            </Route>

            {/* Trip Cost */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Trip cost", "list_permission")} />}>
              <Route path="/trip-cost" element={<TripsCostListing />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Trip cost", "create_permission")} />}>
              <Route path="/cost/create" element={<TripCostCreate />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Trip cost", "view_permission")} />}>
              <Route path="/cost/view" element={<TripCostView />} />
            </Route>

            {/* Milestone */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Milestone", "list_permission")} />}>
              <Route path="/milestones" element={<MilestoneListing />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Milestone", "create_permission")} />}>
              <Route path="/milestone/create" element={<MilestoneCreate />} />
            </Route>

            {/* Live View */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Live view", "list_permission")} />}>
              <Route path="/livetrack" element={<LiveTrack />} />
              <Route path="/triptrack" element={<TripLiveTrack />} />
            </Route>

            {/* Vehicle */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Vehicle", "list_permission")} />}>
              <Route path="/vehicles" element={<VehicleListing />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Vehicle", "create_permission")} />}>
              <Route path="/vehicle/create" element={<VehicleCreate />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Vehicle", "view_permission")} />}>
              <Route path="/vehicle/view" element={<VehicleView />} />
            </Route>

            {/* Customer */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Customer", "list_permission")} />}>
              <Route path="/customers" element={<CustomerList />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Customer", "create_permission")} />}>
              <Route path="/customer/create" element={<CustomerCreate />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Customer", "view_permission")} />}>
              <Route path="/customer/view" element={<CustomerView />} />
            </Route>

            {/* Vendor */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Vendor", "list_permission")} />}>
              <Route path="/vendor" element={<VendorList />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Vendor", "create_permission")} />}>
              <Route path="/vendor/create" element={<VendorCreate />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Vendor", "view_permission")} />}>
              <Route path="/vendor/view" element={<VendorView />} />
            </Route>

            {/* Reports */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Report", "menu_permission")} />}>
              <Route path="/reports" element={<Report />} />
            </Route>

            {/* Products */}
            <Route element={<ProtectedRoute isAllowed={checkPermission("Product", "list_permission")} />}>
              <Route path="/products" element={<ProductListing />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Product", "create_permission")} />}>
              <Route path="/product/create" element={<ProductCreate />} />
            </Route>
            <Route element={<ProtectedRoute isAllowed={checkPermission("Product", "view_permission")} />}>
              <Route path="/product/view" element={<ProductView />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
